import { map, type Observable } from 'rxjs';
import type { RxState } from 'rxdb';

// Home for both sync-status concepts, stored as two keys on the single 'sync' RxState
// object owned by LocalDataService (RxState is persisted and multiInstance-broadcast).
//
// initial-sync latch (INITIAL_SYNC_COMPLETE_KEY): a persistent, device-level fact —
// "a full initial Firestore replication has completed at least once on this device".
// It is a one-way latch and is NEVER reset on app init: a stale-but-complete local
// dataset still gives correct empty-account/deep-link-404 answers, and logout deletes
// the whole RxDB (clearRxDB), so the latch clears with it. Only the leader tab resolves
// awaitInitialReplication in multiInstance mode, so the persisted RxState is what
// propagates the latch to follower tabs, which derive it reactively via get$.
//
// push-pending flag (SYNC_STATE_KEY): session-scoped in-flight-push state that oscillates
// on every autosave ("is anything in flight?"). It IS reset to false on app init (see
// LocalDataService.initSyncState — which must only ever touch this key, never the latch).
export const SYNC_STATE_KEY = 'awaitingSync' as const;
export const INITIAL_SYNC_COMPLETE_KEY = 'initialSyncComplete' as const;

export type SyncStateData = {
  awaitingSync: boolean;
  initialSyncComplete?: boolean;
};

export type SyncState = RxState<SyncStateData>;

export const markInitialSyncComplete = async (syncState: SyncState): Promise<void> => {
  if (syncState.get(INITIAL_SYNC_COMPLETE_KEY) === true) {
    return;
  }
  await syncState.set(INITIAL_SYNC_COMPLETE_KEY, () => true);
};

export const getInitialSyncComplete$ = (syncState: SyncState): Observable<boolean> => {
  return syncState.get$(INITIAL_SYNC_COMPLETE_KEY).pipe(map((value) => value === true));
};

export const isInitialSyncComplete = (syncState: SyncState): boolean => {
  return syncState.get(INITIAL_SYNC_COMPLETE_KEY) === true;
};
